import { useIntl } from 'react-intl'
import cx from "classnames";
import { useSelector } from 'react-redux';
import { useGetHashratesQuery } from '@/shared/api/Hashrates';
import { convertFormattedPowerToMax, getFormattedPower } from '@/shared/utils/formatters';
import { formatChartDate, getBoundaryTimestamps } from '@/shared/utils/dateFormatters';
import CombinedLineChart from '@/shared/ui/charts/CombinedLineChart';
import {
  selectDateRange,
  selectPollingInterval, 
} from "@/processes/Settings";
import { useMinerAccount } from "@/processes/MinerAccounts";
import { useMemo } from "react";

type HashratesChartProps = {
  workerName?: string;
  className?: string;
}

const HashratesChart = (props: HashratesChartProps) => {
  const { 
    workerName = '',
    className,
  } = props;
  const intl = useIntl();
  const dateRange = useSelector(selectDateRange);
  const pollingInterval = useSelector(selectPollingInterval);
  const { account, miner, coin } = useMinerAccount();

  const { data: hashratesData = [], isSuccess } = useGetHashratesQuery({
    coin,
    account,
    miner,
    dateRange,
    worker: workerName,
  }, { pollingInterval, skip: !(account || miner) });

  const maxHashratePower = useMemo(() => (
    Math.max(...hashratesData.map(d => Math.max(d.hashrate, d.meanHashrate, d.reportedHashrate)))
  ), [hashratesData]);
  
  return (
    <>
      {/* TODO: add preloader */}
      <CombinedLineChart
        className={cx('w-full h-[185px] mt-6', className)}
        data={hashratesData}
        XAxisProps={{
          dataKey: 'timestamp',
          formatter: (value) => formatChartDate({ date: value, locale: intl.locale, dateRange, ...getBoundaryTimestamps(hashratesData)}),
        }}
        YAxisProps={{
          dataKey: 'hashrate',
          width: 80,
          interval: 0,
          domain: [0, () => {
            return maxHashratePower * 1.2;
          }],
          formatter: (val) => convertFormattedPowerToMax(maxHashratePower, val),
        }}
        tooltipLabelFormatter={(value) => formatChartDate({ date: value, locale: intl.locale })}
        tooltipValueFormatter={getFormattedPower}
        lines={[
          {
            type: 'AREA',
            name: intl.formatMessage({ id: 'hashrates.realtime' }),
            dataKey: 'hashrate',
            stroke: '#00CCAA',
          },
          {
            type: 'LINE',
            name: intl.formatMessage({ id: 'hashrates.mean' }),
            dataKey: 'meanHashrate',
            stroke: '#FF9500',
          }
        ]}
      />
    </>
  );
}

export default HashratesChart;
